import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Callout } from "@/components/ui/feedback";
import { CopyButton } from "@/components/ui/misc";

export interface ShownSecret {
  endpointId: number;
  name: string;
  secret: string;
  /** A rotation, which retires the secret a receiver may still be checking. */
  rotated: boolean;
}

/**
 * An endpoint's signing secret, the one time core-api shows it: when the endpoint is made, or its
 * secret rotated (ADR-022). Not a page and not in the query cache: it lives exactly as long as this
 * dialog does.
 */
export function WebhookSecretDialog({
  secret, onClose,
}: {
  secret: ShownSecret | undefined;
  onClose: () => void;
}) {
  return (
    <Dialog open={Boolean(secret)} onOpenChange={(open) => !open && onClose()}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{secret?.rotated ? "New signing secret" : "Signing secret"}</DialogTitle>
          <DialogDescription>
            Every delivery to {secret?.name} is signed with it. It is shown this once: a lost secret is
            rotated, never read back.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <p className="flex items-center justify-between gap-2 rounded-md border border-border bg-surface-sunken px-3 py-2">
            <span className="break-all font-mono text-xs text-text">{secret?.secret}</span>
            <CopyButton value={secret?.secret ?? ""} label="Copy the secret" />
          </p>
          {secret?.rotated && (
            <Callout tone="warning">
              Deliveries are signed with this secret from now on. A receiver still checking the old one
              refuses them until it has this one.
            </Callout>
          )}
          <div className="space-y-2 text-xs text-text-muted">
            <p>
              Each delivery carries{" "}
              <code className="font-mono text-text">X-ACL-Signature: t=&lt;unix seconds&gt;,v1=&lt;hex&gt;</code>.
              The hex is an HMAC-SHA256, keyed with this secret, of the <code className="font-mono text-text">t</code>,
              a dot, and the body exactly as it arrived.
            </p>
            <p>
              A receiver that can check it should, and refuse a <code className="font-mono text-text">t</code> more
              than five minutes old. A Zapier catch hook cannot check it: there, the URL itself is the
              secret, so keep it to the people who build the zaps.
            </p>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="default" onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
